import React, { useState, useEffect } from 'react';
import ModalWrapper from 'app/commonComponents/modal/modalWrapper';
import PropTypes from 'prop-types';
import { isEmpty, filter } from 'lodash';
import { ajaxRequest } from 'app/utils/helpers';
import { showError } from 'app/globalMessages/globalMessagesClient';
import LoadingIndicator from 'app/products/components/productList/loadingIndicator';
import './outofstock-notification-modal.scss';

const ProductDetailsModalContent = React.lazy(() =>
  import(
    /* webpackChunkName: "productDetailsModalContent" */
    './productDetailsModalContent/productDetailsModalContent'
  )
);

const ThanksModalContent = React.lazy(() =>
  import(
    /* webpackChunkName: "thanksModalContent" */
    './thanksModalContent'
  )
);

const { backInStockNotificationURL } = window.inlineGlobalConfig;

const OutOfStockNotificationModal = ({ isModalOpen, toggleModal, productDetails }) => {
  const [productDetailsState, setProductDetailsState] = useState(productDetails);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const timeoutRef = React.useRef(null);
  const autoClosingModal = 10000;

  const getOOSSizes = allSizes =>
    allSizes && !isEmpty(allSizes) && filter(allSizes, size => size.count <= 0 || size.stock <= 0);

  // Selected Size should match one of the OOS sizes or be the first in the array
  const getSelectedSize = (sizeSelected, OOSSizes) =>
    isEmpty(sizeSelected) || isEmpty(filter(OOSSizes, size => size.sku === sizeSelected.sku))
      ? OOSSizes[0]
      : sizeSelected;

  const onSizeSelected = sizeSelected => {
    const OOSSizes = getOOSSizes(productDetails.sizes);
    setProductDetailsState({
      ...productDetails,
      selectedSize: getSelectedSize(sizeSelected, OOSSizes),
      sizes: OOSSizes,
    });
  };

  const closeModal = () => {
    setFormSubmitted(false);
    toggleModal();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const submitNotifyMeForm = customerEmail =>
    ajaxRequest('POST', backInStockNotificationURL, {
      customerEmail,
      productCode: productDetailsState.selectedSize.sku,
    }).then(response => {
      setFormSubmitted(true);
      timeoutRef.current = setTimeout(() => closeModal(), autoClosingModal);

      if (response.status === 'error') {
        closeModal();
        window.scrollTo(0, 0);
        showError({
          message: response.message,
          autoHide: true,
          sticky: true,
        });
      }
    });

  useEffect(() => {
    const OOSSizes = getOOSSizes(productDetails.sizes);
    const OOSSelectedSize = getSelectedSize(productDetails.selectedSize, OOSSizes);
    setProductDetailsState({
      ...productDetails,
      sizes: OOSSizes,
      selectedSize: OOSSelectedSize,
    });
  }, [isModalOpen, productDetails]);

  return (
    <ModalWrapper
      customParentClass="oos-notification-modal"
      isModalOpen={isModalOpen}
      modalToggle={closeModal}
      isWithinSticky
    >
      <React.Suspense fallback={<LoadingIndicator fallbackUi />}>
        {formSubmitted === true ? (
          <ThanksModalContent toggleModal={closeModal} />
        ) : (
          <ProductDetailsModalContent
            onBackBtnClick={closeModal}
            submitNotifyMeForm={submitNotifyMeForm}
            productDetails={productDetailsState}
            onSizeSelected={onSizeSelected}
          />
        )}
      </React.Suspense>
    </ModalWrapper>
  );
};

OutOfStockNotificationModal.defaultProps = {
  isModalOpen: false,
  productDetails: {
    selectedSize: {},
    hasStock: true,
    sizes: [],
    heroImage: '',
    name: '',
    colourName: '',
    description: '',
    price: '',
  },
};

OutOfStockNotificationModal.propTypes = {
  isModalOpen: PropTypes.bool,
  toggleModal: PropTypes.func.isRequired,
  productDetails: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default OutOfStockNotificationModal;
